/**
 * ComponentRouter
 *
 * Central registry mapping entity types to components.
 * This is the single source of truth for component routing.
 *
 * NAMING CONVENTION:
 * - Template: Layout components ignorant of content (page, header, footer, sidebar, list, grid)
 * - Row: List/table/grid row components (TaskRow, ProjectRow, ContactRow)
 * - Widget: Forms that appear when clicking a row (TaskWidget, ProjectWidget, ContactWidget)
 *
 * Usage:
 * - renderEntityRow(entity) → Renders the appropriate Row component
 * - getEntityWidget(entityId) → Returns the appropriate Widget component
 * - getEntityTypeName(entityId) → Returns human-readable type name
 */

import React, { ComponentType, ReactNode } from 'react';
import { getPrefix, getTypeName, type IdPrefix } from '@business-assistant/shared/domain';

// Import existing components
import { TaskRow } from '../../../components/tasks/TaskRow';
import { ProjectRow } from '../../../components/rows/ProjectRow';
import { ContactRow } from '../../../components/rows/ContactRow';
import { CallRow } from '../../../components/rows/CallRow';
import { EmailRow } from '../../../components/rows/EmailRow';
import { MessageRow } from '../../../components/rows/MessageRow';
import { InvoiceRow } from '../../../components/rows/InvoiceRow';
import { TimeEntryRow } from '../../../components/rows/TimeEntryRow';
import { MilestoneRow } from '../../../components/rows/MilestoneRow';
import { ChatRow } from '../../../components/rows/ChatRow';
import { EventRow } from '../../../components/rows/EventRow';
import { MovementRow } from '../../../components/rows/MovementRow';
import { ConversationRow } from '../../../components/rows/ConversationRow';
import { VideoCallRow } from '../../../components/videocall/VideoCallRow';

// Import widgets
import { TaskWidget } from '../../../components/projects/TaskWidget';
import { ProjectWidget } from '../../../components/projects/ProjectWidget';
import { ContactWidget } from '../../../components/hub/ContactWidget';
import { CallWidget } from '../../../components/hub/CallWidget';
import { EmailWidget } from '../../../components/hub/EmailWidget';
import { ConversationWidget } from '../../../components/hub/ConversationWidget';
import { InvoiceWidget } from '../../../components/accounting/InvoiceWidget';
import { MovementWidget } from '../../../components/accounting/MovementWidget';
import { TimeEntryWidget } from '../../../components/projects/TimeEntryWidget';
import { MilestoneWidget } from '../../../components/projects/MilestoneWidget';
import { EventWidget } from '../../../components/calendar/EventWidget';
import { TicketWidget } from '../../../components/projects/ProjectForms';
import { MessageWidget } from '../../../components/hub/MessageWidget';
import { ChatWidget } from '../../../components/hub/ChatWidget';
import { VideoCallWidget } from '../../../components/videocall/VideoCallWidget';

// =============================================================================
// ROW COMPONENT REGISTRY
// =============================================================================

/**
 * Map of ID prefix → Row component
 *
 * NOTE: Components marked as TODO will use fallback PlaceholderRow until implemented.
 */
export const ROW_COMPONENTS: Partial<Record<IdPrefix, ComponentType<any>>> = {
  // Projects module
  tsk: TaskRow,
  prj: ProjectRow,
  tme: TimeEntryRow,
  mst: MilestoneRow,

  // Hub module
  cnt: ContactRow,
  cal: CallRow,
  vcl: VideoCallRow,
  eml: EmailRow,
  msg: MessageRow,
  cht: ChatRow,
  cnv: ConversationRow,

  // Calendar module
  evt: EventRow,

  // Accounting module
  inv: InvoiceRow,
  mov: MovementRow,
};

/**
 * Fallback placeholder row for entities without a registered component
 */
function PlaceholderRow({ entity }: { entity: any }) {
  const prefix = getPrefix(entity.id);
  const typeName = prefix ? getTypeName(entity.id) : 'Unknown';

  return (
    <div
      style={{
        padding: '8px 12px',
        borderBottom: '1px solid #e8e8e8',
        background: '#fafafa',
        fontSize: '13px',
        color: '#666',
      }}
    >
      [{typeName}] {entity.title || entity.name || entity.displayName || entity.id}
      <span style={{ marginLeft: '8px', fontSize: '11px', color: '#999' }}>
        (Row component not implemented)
      </span>
    </div>
  );
}

// =============================================================================
// WIDGET COMPONENT REGISTRY
// =============================================================================

/**
 * Map of ID prefix → Widget component
 *
 * Widgets are forms that appear when clicking on a Row.
 * Each Row should have a corresponding Widget.
 *
 * NOTE: Widgets marked as TODO will return null until implemented.
 */
export const WIDGET_COMPONENTS: Partial<Record<IdPrefix, ComponentType<any>>> = {
  // Projects module
  tsk: TaskWidget,
  prj: ProjectWidget,
  tme: TimeEntryWidget,
  mst: MilestoneWidget,
  tkt: TicketWidget,

  // Hub module
  cnt: ContactWidget,
  cal: CallWidget,
  vcl: VideoCallWidget,
  eml: EmailWidget,
  cnv: ConversationWidget,
  msg: MessageWidget,
  cht: ChatWidget,

  // Calendar module
  evt: EventWidget,

  // Accounting module
  inv: InvoiceWidget,
  mov: MovementWidget,
};

// =============================================================================
// ROUTER FUNCTIONS
// =============================================================================

/**
 * Renders the appropriate row component for an entity
 *
 * @param entity - Entity object with 'id' property
 * @param props - Additional props to pass to the row component
 * @returns ReactNode or null
 *
 * @example
 * ```tsx
 * const task = { id: 'tsk-123', title: 'Fix bug' };
 * return renderEntityRow(task, { onTitleClick: handleClick });
 * ```
 */
export function renderEntityRow(entity: any, props?: any): ReactNode {
  if (!entity || !entity.id) {
    console.warn('renderEntityRow: Invalid entity', entity);
    return null;
  }

  const prefix = getPrefix(entity.id);
  if (!prefix) {
    console.warn('renderEntityRow: Unknown ID prefix', entity.id);
    return null;
  }

  // Get registered component or use placeholder
  const RowComponent = ROW_COMPONENTS[prefix] || PlaceholderRow;

  return <RowComponent key={entity.id} {...props} entity={entity} />;
}

/**
 * Gets the widget component for an entity type
 *
 * @param entityId - Entity ID to get widget for
 * @returns Widget component or null
 *
 * @example
 * ```tsx
 * const WidgetComponent = getEntityWidget('tsk-123');
 * if (WidgetComponent) {
 *   return <WidgetComponent onSave={handleSave} onCancel={handleCancel} />;
 * }
 * ```
 */
export function getEntityWidget(entityId: string): ComponentType<any> | null {
  const prefix = getPrefix(entityId);
  if (!prefix) return null;

  return WIDGET_COMPONENTS[prefix] || null;
}

/**
 * Gets human-readable entity type name from ID
 *
 * @param entityId - Entity ID
 * @returns Type name or null
 *
 * @example
 * ```tsx
 * getEntityTypeName('tsk-123') // => 'Task'
 * getEntityTypeName('prj-456') // => 'Project'
 * ```
 */
export function getEntityTypeName(entityId: string): string | null {
  return getTypeName(entityId);
}

/**
 * Checks if a row component is registered for an entity type
 *
 * @param prefix - Entity ID prefix
 * @returns true if component exists
 */
export function hasRowComponent(prefix: IdPrefix): boolean {
  return prefix in ROW_COMPONENTS;
}

/**
 * Checks if a widget component is registered for an entity type
 *
 * @param prefix - Entity ID prefix
 * @returns true if component exists
 */
export function hasWidgetComponent(prefix: IdPrefix): boolean {
  return prefix in WIDGET_COMPONENTS;
}

/**
 * Gets all registered row component prefixes
 *
 * @returns Array of registered prefixes
 */
export function getRegisteredRowPrefixes(): IdPrefix[] {
  return Object.keys(ROW_COMPONENTS) as IdPrefix[];
}

/**
 * Gets all registered widget component prefixes
 *
 * @returns Array of registered prefixes
 */
export function getRegisteredWidgetPrefixes(): IdPrefix[] {
  return Object.keys(WIDGET_COMPONENTS) as IdPrefix[];
}

/**
 * Props for renderEntityWidget
 */
export interface RenderWidgetProps {
  onClose: () => void;
  onSave?: (data: any) => void;
}

/**
 * Props for renderWidgetByType (for creating new entities)
 */
export interface RenderWidgetByTypeProps extends RenderWidgetProps {
  entityId?: string;
  projectId?: string;
}

/**
 * Renders the appropriate widget component for an entity
 *
 * Each widget type has different required props:
 * - TaskWidget: projectId, taskId?, onClose, onSave
 * - ProjectWidget: projectId?, onClose, onSave
 * - ContactWidget: isOpen, contactId?, onClose, onSave
 * - CallWidget: isOpen, callId?, onClose, onSave
 * - EmailWidget: isOpen, emailId?, onClose, onSave
 * - InvoiceWidget: invoiceId?, projectId?, onClose, onSave
 * - TimeEntryWidget: isOpen, projectId, timeEntryId?, onClose, onSave
 * - MilestoneWidget: isOpen, projectId, milestoneId?, onClose, onSave
 * - EventWidget: isOpen, eventId?, onClose, onSave
 *
 * @param entity - Entity object with 'id' property
 * @param props - Common props (onClose, onSave)
 * @returns ReactNode or null
 */
export function renderEntityWidget(
  entity: any,
  props: RenderWidgetProps
): ReactNode {
  if (!entity || !entity.id) {
    console.warn('renderEntityWidget: Invalid entity', entity);
    return null;
  }

  const prefix = getPrefix(entity.id);
  if (!prefix) {
    console.warn('renderEntityWidget: Unknown ID prefix', entity.id);
    return null;
  }

  const WidgetComponent = WIDGET_COMPONENTS[prefix];
  if (!WidgetComponent) {
    console.warn('renderEntityWidget: No widget registered for prefix', prefix);
    return null;
  }

  // Build widget-specific props based on entity type
  const baseProps = {
    onClose: props.onClose,
    onSave: props.onSave || (() => props.onClose()),
  };

  switch (prefix) {
    case 'tsk':
      return (
        <WidgetComponent
          key={entity.id}
          projectId={entity.projectId || ''}
          taskId={entity.id}
          {...baseProps}
        />
      );

    case 'prj':
      return (
        <WidgetComponent
          key={entity.id}
          projectId={entity.id}
          {...baseProps}
        />
      );

    case 'cnt':
      return (
        <WidgetComponent
          key={entity.id}
          isOpen={true}
          contactId={entity.id}
          {...baseProps}
        />
      );

    case 'cal':
      return (
        <WidgetComponent
          key={entity.id}
          isOpen={true}
          callId={entity.id}
          call={entity}
          {...baseProps}
        />
      );

    case 'vcl':
      return (
        <WidgetComponent
          key={entity.id}
          isOpen={true}
          meetingId={entity.id}
          meeting={entity}
          {...baseProps}
        />
      );

    case 'eml':
      return (
        <WidgetComponent
          key={entity.id}
          isOpen={true}
          emailId={entity.id}
          email={entity}
          {...baseProps}
        />
      );

    case 'cnv':
      return (
        <WidgetComponent
          key={entity.id}
          conversationId={entity.id}
          {...baseProps}
        />
      );

    case 'msg':
      return (
        <WidgetComponent
          key={entity.id}
          messageId={entity.id}
          message={entity}
          {...baseProps}
        />
      );

    case 'cht':
      return (
        <WidgetComponent
          key={entity.id}
          chatId={entity.id}
          chat={entity}
          {...baseProps}
        />
      );

    case 'inv':
      return (
        <WidgetComponent
          key={entity.id}
          invoiceId={entity.id}
          projectId={entity.projectId}
          {...baseProps}
        />
      );

    case 'mov':
      return (
        <WidgetComponent
          key={entity.id}
          movementId={entity.id}
          {...baseProps}
        />
      );

    case 'tme':
      return (
        <WidgetComponent
          key={entity.id}
          isOpen={true}
          projectId={entity.projectId || ''}
          timeEntryId={entity.id}
          {...baseProps}
        />
      );

    case 'mst':
      return (
        <WidgetComponent
          key={entity.id}
          isOpen={true}
          projectId={entity.projectId || ''}
          milestoneId={entity.id}
          {...baseProps}
        />
      );

    case 'evt':
      return (
        <WidgetComponent
          key={entity.id}
          isOpen={true}
          eventId={entity.id}
          {...baseProps}
        />
      );

    case 'tkt':
      return (
        <WidgetComponent
          key={entity.id}
          isOpen={true}
          ticketId={entity.id}
          {...baseProps}
        />
      );

    default:
      // Fallback: try to render with common props
      return (
        <WidgetComponent
          key={entity.id}
          isOpen={true}
          entityId={entity.id}
          {...baseProps}
        />
      );
  }
}

/**
 * Renders a widget by entity type prefix
 *
 * Use this for creating new entities or when you only have the ID, not the full entity.
 * For editing existing entities with full data, prefer renderEntityWidget().
 *
 * @param entityType - Entity type prefix (e.g., 'tsk', 'cnt', 'inv')
 * @param props - Widget props including onClose, onSave, entityId (optional), projectId (optional)
 * @returns ReactNode or null
 *
 * @example
 * ```tsx
 * // Create new contact
 * renderWidgetByType('cnt', { onClose: handleClose, onSave: handleSave });
 *
 * // Edit existing contact
 * renderWidgetByType('cnt', { entityId: 'cnt-123', onClose: handleClose, onSave: handleSave });
 * ```
 */
export function renderWidgetByType(
  entityType: IdPrefix,
  props: RenderWidgetByTypeProps
): ReactNode {
  const WidgetComponent = WIDGET_COMPONENTS[entityType];
  if (!WidgetComponent) {
    console.warn('renderWidgetByType: No widget registered for type', entityType);
    return null;
  }

  const baseProps = {
    onClose: props.onClose,
    onSave: props.onSave || (() => props.onClose()),
  };

  switch (entityType) {
    case 'tsk':
      return (
        <WidgetComponent
          key={props.entityId || 'new-task'}
          projectId={props.projectId || ''}
          taskId={props.entityId}
          {...baseProps}
        />
      );

    case 'prj':
      return (
        <WidgetComponent
          key={props.entityId || 'new-project'}
          projectId={props.entityId}
          {...baseProps}
        />
      );

    case 'cnt':
      return (
        <WidgetComponent
          key={props.entityId || 'new-contact'}
          isOpen={true}
          contactId={props.entityId}
          {...baseProps}
        />
      );

    case 'cal':
      return (
        <WidgetComponent
          key={props.entityId || 'new-call'}
          isOpen={true}
          callId={props.entityId}
          {...baseProps}
        />
      );

    case 'vcl':
      return (
        <WidgetComponent
          key={props.entityId || 'new-videocall'}
          isOpen={true}
          meetingId={props.entityId}
          {...baseProps}
        />
      );

    case 'eml':
      return (
        <WidgetComponent
          key={props.entityId || 'new-email'}
          isOpen={true}
          emailId={props.entityId}
          {...baseProps}
        />
      );

    case 'cnv':
      return (
        <WidgetComponent
          key={props.entityId || 'new-conversation'}
          conversationId={props.entityId}
          {...baseProps}
        />
      );

    case 'msg':
      return (
        <WidgetComponent
          key={props.entityId || 'new-message'}
          messageId={props.entityId}
          {...baseProps}
        />
      );

    case 'cht':
      return (
        <WidgetComponent
          key={props.entityId || 'new-chat'}
          chatId={props.entityId}
          {...baseProps}
        />
      );

    case 'inv':
      return (
        <WidgetComponent
          key={props.entityId || 'new-invoice'}
          invoiceId={props.entityId}
          projectId={props.projectId}
          {...baseProps}
        />
      );

    case 'mov':
      return (
        <WidgetComponent
          key={props.entityId || 'new-movement'}
          movementId={props.entityId}
          {...baseProps}
        />
      );

    case 'tme':
      return (
        <WidgetComponent
          key={props.entityId || 'new-time-entry'}
          isOpen={true}
          projectId={props.projectId || ''}
          timeEntryId={props.entityId}
          {...baseProps}
        />
      );

    case 'mst':
      return (
        <WidgetComponent
          key={props.entityId || 'new-milestone'}
          isOpen={true}
          projectId={props.projectId || ''}
          milestoneId={props.entityId}
          {...baseProps}
        />
      );

    case 'evt':
      return (
        <WidgetComponent
          key={props.entityId || 'new-event'}
          isOpen={true}
          eventId={props.entityId}
          {...baseProps}
        />
      );

    case 'tkt':
      return (
        <WidgetComponent
          key={props.entityId || 'new-ticket'}
          isOpen={true}
          ticketId={props.entityId}
          {...baseProps}
        />
      );

    default:
      return (
        <WidgetComponent
          key={props.entityId || 'new-entity'}
          isOpen={true}
          entityId={props.entityId}
          {...baseProps}
        />
      );
  }
}
